import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { ASUHeader } from "@asu/component-header/dist/asuHeader.es";
import { Footer } from "../Footer";

import desktopLogo from "../../../assets/img/arizona-state-university-logo-vertical.png";
import mobileLogo from "../../../assets/img/arizona-state-university-logo.png";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Cookies from "js-cookie";

import parseHtml from "html-react-parser";
import { stripHtml } from "string-strip-html";
import { isInternalLink } from "is-internal-link";

import ErrorPage from "../ErrorPage";

const HeaderSection = styled.div`
  header {
    position: sticky;

    .content-container {
      font-size: 1rem;
      border: 0;
      margin: 0;
      height: fit-content;
      align-items: start;
      flex-direction: row;
    }

    a {
      cursor: pointer;
    }
  }
`;

function Content(props) {
  const history = useHistory();
  const location = useLocation();

  const [isAuthenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(Cookies.get("username"));
  const [targetNID, setTargetNID] = useState();
  const [menu, setMenu] = useState(props.data.menu);
  const [isCustom, setCustom] = useState(props.custom);

  const handleNavClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setTargetNID(item.nid);
    const path = isCustom ? item.alias : `/${props.group}${item.alias}`;
    history.push(path);
  };

  /*
  const findNavDestination = (target, menu) => {
    for (let i = 0; i < menu.length; i++) {
      const item = menu[i];

      // Recurse into submenus
      if (Array.isArray(item.items) && item.items.length) {
        const found = findNavDestination(target, item.items);
        if (found) return true;
      }

      // Match on title
      if (target === item.title) {
        setTargetNID(item.nid);
        const path = isCustom ? item.alias : `/${props.group}${item.alias}`;
        console.log("Navigating to:", path);
        history.push(path);
        return true;
      }
    }
    return false;
  };
  */

  const buildNav = (items) => {
    let navTree = [];
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].hidden) continue; // skip hidden items
        if (Array.isArray(items[i].items)) {
          navTree.push({
            text: items[i].title,
            href: "#",
            items: [buildNav(items[i].items)],
          });
        } else {
          navTree.push(buildNavNode(items[i], isCustom, i === 0));
        }
      }
    }
    return navTree;
  };

  const aliasToNid = useMemo(() => {
    const map = new Map();
    const walk = (items = []) => {
      for (const it of items) {
        if (!it) continue;

        // index ALL internal items, hidden or not
        if (!Array.isArray(it.items) && isInternalLink(it.alias)) {
          const full = isCustom ? it.alias : `/${props.group}${it.alias}`;
          map.set(full, it.nid);
        }

        if (Array.isArray(it.items) && it.items.length) {
          walk(it.items);
        }
      }
    };
    if (Array.isArray(menu)) walk(menu);
      return map;
  }, [menu, isCustom, props.group]);

  const buildNavNode = (item, isCustom, isFirst) => {
    const title = item.title;
    const alias = item.alias;
    const isHidden = item.hidden === true;

    const path = decodeURIComponent(window.location.pathname);
    let isSelected = false;

    if (
      (isCustom && (path === alias || (isFirst && path === "/"))) ||
      (!isCustom &&
        (path === "/" + props.group + alias ||
          (isFirst && path === "/" + props.group)))
    ) {
      isSelected = true;
    }

    let navClass = "";
    if (isInternalLink(alias)) {
      navClass = isHidden ? "hidden" : "";
      return {
        onClick: (e) => handleNavClick(e, item),
        text: title,
        href: isCustom ? alias : "/" + props.group + alias,
        type: "",
        selected: isSelected,
        ...(navClass && { class: navClass })
      };
    } else {
      navClass = isHidden ? "externalLink hidden" : "externalLink";
      return {
        text: title,
        href: alias,
        class: navClass,
      };
    }
  };

  const setExternalTargets = () => {
    const links = Array.from(document.querySelectorAll("a.externalLink"));
    links.forEach((element) => {
      element.setAttribute("target", "_blank");
    });
  };

  useEffect(() => {
    const path = decodeURIComponent(location.pathname);
    const rootPath = isCustom ? "/" : `/${props.group}`;

    // 1) Direct alias match wins (hidden allowed)
    const matchedNid = aliasToNid.get(path);
    if (matchedNid && matchedNid !== targetNID) {
      setTargetNID(matchedNid);
      return;
    }

    // 2) Root-only fallback → first VISIBLE internal page
    if (path === rootPath) {
      const firstVisible = (() => {
        const find = (items = []) => {
          for (const it of items) {
            if (!it || it.hidden) continue;                 // skip hidden in fallback
            if (Array.isArray(it.items) && it.items.length) {
              const nid = find(it.items);
              if (nid) return nid;
            } else if (isInternalLink(it.alias)) {
              return it.nid;                                // first visible internal page
            }
          }
          return null;
        };
        return Array.isArray(menu) ? find(menu) : null;
      })();

      if (firstVisible && firstVisible !== targetNID) {
        setTargetNID(firstVisible);
      }
    }
  }, [location.pathname, aliasToNid, menu, isCustom, props.group, targetNID]);

  useEffect(() => {
    setExternalTargets();
  }, []);

  useEffect(() => {
    document.title = props.data.site_name;
  }, [props.data.site_name]);

  const nav = buildNav(menu);

  let content = "";
  let redirect = false;

  if (Array.isArray(props.data.redirects)) {
    props.data.redirects.forEach((i) => {
      if (props.page === i.path) {
        redirect = true;
        window.location.replace(i.target);
      }
    });
  }

  if (Array.isArray(props.data.content)) {
    props.data.content.forEach((i) => {
      if (targetNID === i.nid) {
        content = parseHtml(i.content);
      }
    });
  }

  const baseURL = !isCustom ? "/" + props.group + "/" : "/";

  return (
    <div>
      <HeaderSection>
        <ASUHeader
          logo={{
            alt: "logo",
            src: desktopLogo,
            mobileSrc: mobileLogo,
          }}
          baseUrl={baseURL}
          loggedIn={isAuthenticated}
          userName={user}
          onLoginClick={() => {
            window.open("https://sites.thecollege.asu.edu/cas");
          }}
          navTree={nav}
          title={props.data.site_name}
          buttons={[]}
          searchUrl={"https://search.asu.edu/search"}
        />
      </HeaderSection>

      {props.status === 500 ? (
        <ErrorPage {...props} error="" custom={isCustom} />
      ) : content.length === 0 ? (
        redirect ? (
          <ErrorPage {...props} error="304" custom={isCustom} />
        ) : props.page === undefined ? (
          <ErrorPage {...props} error="500" custom={isCustom} />
        ) : (
          <ErrorPage {...props} error="404" custom={isCustom} />
        )
      ) : (
        <div className="main-container">{content}</div>
      )}

      <Footer />
    </div>
  );
}

export { Content };